"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useBinanceOrderBook } from "@/hooks/useBinanceOrderBook";
interface LiquidityHeatmapProps {
  symbol: string;
  tokenName: string;
}
interface Bucket {
  priceLow: number;
  priceHigh: number;
  bidNotional: number;
  askNotional: number;
}
function formatUsd(value: number): string {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}
function formatPrice(price: number): string {
  if (price < 0.01) return price.toFixed(6);
  if (price < 1) return price.toFixed(4);
  if (price < 100) return price.toFixed(3);
  return price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
export function LiquidityHeatmap(t0) {
  const $ = _c(71);
  const {
    symbol,
    tokenName
  } = t0;
  const {
    snapshot,
    loading,
    error
  } = useBinanceOrderBook(symbol, 1000, 15000);
  let t1;
  bb0: {
    if (!snapshot) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      let t3;
      if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = {
          buckets: t2 as Bucket[],
          maxBucketNotional: 0,
          midPrice: 0,
          priceRange: {
            min: 0,
            max: 0
          }
        };
        $[1] = t3;
      } else {
        t3 = $[1];
      }
      t1 = t3;
      break bb0;
    }
    const allLevels = [...snapshot.bids, ...snapshot.asks];
    if (allLevels.length === 0) {
      let t2;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[2] = t2;
      } else {
        t2 = $[2];
      }
      let t3;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = {
          min: 0,
          max: 0
        };
        $[3] = t3;
      } else {
        t3 = $[3];
      }
      let t4;
      if ($[4] !== snapshot.midPrice) {
        t4 = {
          buckets: t2 as Bucket[],
          maxBucketNotional: 0,
          midPrice: snapshot.midPrice,
          priceRange: t3
        };
        $[4] = snapshot.midPrice;
        $[5] = t4;
      } else {
        t4 = $[5];
      }
      t1 = t4;
      break bb0;
    }
    const mid = snapshot.midPrice;
    const minPrice = mid * 0.95;
    const maxPrice = mid * 1.05;
    const bucketSize = (maxPrice - minPrice) / 40;
    let maxNotional;
    let t2;
    if ($[6] !== bucketSize || $[7] !== maxPrice || $[8] !== minPrice || $[9] !== snapshot.asks || $[10] !== snapshot.bids) {
      const bucketArr = [];
      for (let i = 0; i < 40; i++) {
        bucketArr.push({
          priceLow: minPrice + i * bucketSize,
          priceHigh: minPrice + (i + 1) * bucketSize,
          bidNotional: 0,
          askNotional: 0
        });
      }
      for (const bid of snapshot.bids) {
        if (bid.price < minPrice || bid.price > maxPrice) {
          continue;
        }
        const idx = Math.min(39, Math.floor((bid.price - minPrice) / bucketSize));
        bucketArr[idx].bidNotional = bucketArr[idx].bidNotional + bid.notional;
      }
      for (const ask of snapshot.asks) {
        if (ask.price < minPrice || ask.price > maxPrice) {
          continue;
        }
        const idx_0 = Math.min(39, Math.floor((ask.price - minPrice) / bucketSize));
        bucketArr[idx_0].askNotional = bucketArr[idx_0].askNotional + ask.notional;
      }
      maxNotional = Math.max(...bucketArr.map(_temp));
      t2 = bucketArr.reverse();
      $[6] = bucketSize;
      $[7] = maxPrice;
      $[8] = minPrice;
      $[9] = snapshot.asks;
      $[10] = snapshot.bids;
      $[11] = maxNotional;
      $[12] = t2;
    } else {
      maxNotional = $[11];
      t2 = $[12];
    }
    let t3;
    if ($[13] !== maxPrice || $[14] !== minPrice) {
      t3 = {
        min: minPrice,
        max: maxPrice
      };
      $[13] = maxPrice;
      $[14] = minPrice;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    let t4;
    if ($[16] !== maxNotional || $[17] !== mid || $[18] !== t2 || $[19] !== t3) {
      t4 = {
        buckets: t2,
        maxBucketNotional: maxNotional,
        midPrice: mid,
        priceRange: t3
      };
      $[16] = maxNotional;
      $[17] = mid;
      $[18] = t2;
      $[19] = t3;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    t1 = t4;
  }
  const {
    buckets,
    maxBucketNotional,
    midPrice
  } = t1;
  if (error === "not_available") {
    let t2;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-6"><h3 className="text-lg font-semibold text-white mb-2">Liquidity Heatmap</h3><p className="text-sm text-gray-500">Order book data unavailable for this token. Liquidity heatmaps require a Binance spot pair.</p></div>;
      $[21] = t2;
    } else {
      t2 = $[21];
    }
    return t2;
  }
  if (loading || !snapshot) {
    let t2;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-6 animate-pulse"><div className="h-5 bg-[hsl(230,15%,14%)] rounded w-1/3 mb-3" /><div className="h-96 bg-[hsl(230,15%,10%)] rounded" /></div>;
      $[22] = t2;
    } else {
      t2 = $[22];
    }
    return t2;
  }
  const spread = snapshot.asks[0] && snapshot.bids[0] ? (snapshot.asks[0].price - snapshot.bids[0].price) / snapshot.midPrice * 100 : 0;
  let t2;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h3 className="text-base sm:text-lg font-semibold text-white">Liquidity Heatmap</h3>;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  let t3;
  if ($[24] !== tokenName) {
    t3 = <div>{t2}<p className="text-xs text-gray-500 mt-0.5">{tokenName} order book depth (±5% from mid), live every 15s</p></div>;
    $[24] = tokenName;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  let t4;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center gap-1.5"><span className="w-3 h-3 shrink-0 rounded-sm bg-emerald-500" /><span className="text-gray-400">Bid liquidity</span></div>;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  let t5;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">{t4}<div className="flex items-center gap-1.5"><span className="w-3 h-3 shrink-0 rounded-sm bg-red-500" /><span className="text-gray-400">Ask liquidity</span></div></div>;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== t3) {
    t6 = <div className="flex items-center justify-between flex-wrap gap-2">{t3}{t5}</div>;
    $[28] = t3;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p className="text-[11px] text-gray-500 uppercase tracking-wide">Mid Price</p>;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== midPrice) {
    t8 = formatPrice(midPrice);
    $[31] = midPrice;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== t8) {
    t9 = <div className="bg-[hsl(230,15%,10%)] rounded-lg px-3 py-2 min-w-0">{t7}<p className="text-sm font-mono font-semibold text-white break-words">${t8}</p></div>;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <p className="text-[11px] text-gray-500 uppercase tracking-wide">Spread</p>;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== spread) {
    t11 = spread.toFixed(3);
    $[36] = spread;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== t11) {
    t12 = <div className="bg-[hsl(230,15%,10%)] rounded-lg px-3 py-2 min-w-0">{t10}<p className="text-sm font-mono font-semibold text-white">{t11}%</p></div>;
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <p className="text-[11px] text-emerald-500 uppercase tracking-wide">Bid Depth</p>;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== snapshot.totalBidLiquidity) {
    t14 = formatUsd(snapshot.totalBidLiquidity);
    $[41] = snapshot.totalBidLiquidity;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t14) {
    t15 = <div className="bg-[hsl(230,15%,10%)] rounded-lg px-3 py-2 min-w-0">{t13}<p className="text-sm font-mono font-semibold text-emerald-400 break-words">{t14}</p></div>;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <p className="text-[11px] text-red-500 uppercase tracking-wide">Ask Depth</p>;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== snapshot.totalAskLiquidity) {
    t17 = formatUsd(snapshot.totalAskLiquidity);
    $[46] = snapshot.totalAskLiquidity;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t17) {
    t18 = <div className="bg-[hsl(230,15%,10%)] rounded-lg px-3 py-2 min-w-0">{t16}<p className="text-sm font-mono font-semibold text-red-400 break-words">{t17}</p></div>;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t12 || $[51] !== t15 || $[52] !== t18 || $[53] !== t9) {
    t19 = <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">{t9}{t12}{t15}{t18}</div>;
    $[50] = t12;
    $[51] = t15;
    $[52] = t18;
    $[53] = t9;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== t19 || $[56] !== t6) {
    t20 = <div className="p-4 border-b border-[hsl(var(--border))]">{t6}{t19}</div>;
    $[55] = t19;
    $[56] = t6;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== buckets || $[59] !== maxBucketNotional || $[60] !== midPrice) {
    let t22;
    if ($[62] !== maxBucketNotional || $[63] !== midPrice) {
      t22 = (bucket, i_0) => {
        const bidIntensity = maxBucketNotional > 0 ? bucket.bidNotional / maxBucketNotional : 0;
        const askIntensity = maxBucketNotional > 0 ? bucket.askNotional / maxBucketNotional : 0;
        const isMidBucket = midPrice >= bucket.priceLow && midPrice <= bucket.priceHigh;
        const avgPrice = (bucket.priceLow + bucket.priceHigh) / 2;
        return <div key={i_0} className={`relative flex items-center h-5 text-[11px] font-mono ${isMidBucket ? "border-y border-yellow-500/50" : ""}`}><div className="w-16 sm:w-20 pr-1.5 sm:pr-2 text-right text-gray-500 shrink-0 truncate">${formatPrice(avgPrice)}</div><div className="flex-1 relative h-full flex"><div className="flex-1 h-full flex justify-end pr-px">{bidIntensity > 0 && <div className="h-full rounded-sm transition-all" style={{
                width: `${bidIntensity * 100}%`,
                backgroundColor: `rgba(34, 197, 94, ${Math.max(0.15, bidIntensity)})`,
                boxShadow: bidIntensity > 0.7 ? "0 0 8px rgba(34, 197, 94, 0.5)" : undefined
              }} />}</div><div className={`w-px h-full ${isMidBucket ? "bg-yellow-500" : "bg-[hsl(230,15%,18%)]"}`} /><div className="flex-1 h-full flex justify-start pl-px">{askIntensity > 0 && <div className="h-full rounded-sm transition-all" style={{
                width: `${askIntensity * 100}%`,
                backgroundColor: `rgba(239, 68, 68, ${Math.max(0.15, askIntensity)})`,
                boxShadow: askIntensity > 0.7 ? "0 0 8px rgba(239, 68, 68, 0.5)" : undefined
              }} />}</div></div><div className="w-16 sm:w-24 pl-1.5 sm:pl-2 text-left text-gray-500 shrink-0 truncate">{bucket.bidNotional > 0 || bucket.askNotional > 0 ? formatUsd(Math.max(bucket.bidNotional, bucket.askNotional)) : ""}</div></div>;
      };
      $[62] = maxBucketNotional;
      $[63] = midPrice;
      $[64] = t22;
    } else {
      t22 = $[64];
    }
    t21 = buckets.map(t22);
    $[58] = buckets;
    $[59] = maxBucketNotional;
    $[60] = midPrice;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[65] !== t21) {
    t22 = <div className="p-4"><div className="space-y-0.5">{t21}</div></div>;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="px-4 py-2 border-t border-[hsl(var(--border))] text-[11px] text-gray-500">Yellow band = current mid price. Brighter bars indicate stronger walls where large orders are clustered. Watch for asymmetries to spot accumulation or distribution zones.</div>;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== t20 || $[69] !== t22) {
    t24 = <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] overflow-hidden">{t20}{t22}{t23}</div>;
    $[68] = t20;
    $[69] = t22;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  return t24;
}
function _temp(b) {
  return Math.max(b.bidNotional, b.askNotional);
}